import { Link, NavLink, useNavigate } from 'react-router-dom';
import useAuthHook from '../../api/auth';
import useAuth from '../../hooks/useAuth';
import LanguageSwitcher from "./LanguageSwitcher";
import useAdminUserCategoriesHook from '../../api/panel/userCategories';
import useInbox from '../../api/panel/inbox';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useMemo, useState } from "react";
import NotificationBell from '../admin/notifications/NotificationBell';
import CurationShortcut from '../admin/curations/CurationShortcut';
import packageJson from '../../../package.json';
import '../../css/global/side-nav.css';

const APP_VERSION = packageJson.version.split('.').slice(0, 2).join('.');
import {
    FaChevronRight, FaBars, FaSignInAlt, FaUser, FaSignOutAlt, FaRocket, FaUserCog,
    FaInfoCircle, FaCalendarAlt, FaTools, FaUsers, FaCogs, FaKey, FaLanguage, FaUserShield, FaUserCircle, FaBell, FaCubes,
    FaTachometerAlt
} from 'react-icons/fa';

const Navigation = () => {
    const navigate = useNavigate();
    const { postLogout } = useAuthHook();
    const { auth } = useAuth();
    const { t } = useTranslation();

    const iconForAdmin = (item) => {
        const key = (item?.menu || item?.path || '').toLowerCase();
        // console.log(item);
        // console.log(key);

        if (key.includes('users')) return <FaUsers />;
        if (key.includes('roles')) return <FaUserShield />;
        if (key.includes('profiles')) return <FaUserCircle />;
        if (key.includes('invites')) return <FaUsers />;
        if (key.includes('moderator')) return <FaCubes />;
        if (key.includes('settings') || key.includes('config')) return <FaCogs />;
        if (key.includes('translation')) return <FaLanguage />;
        if (key.includes('permission')) return <FaKey />;
        if (key.includes('lab')) return <FaRocket />;
        if (key.includes('account') || key.includes('profile')) return <FaUserCircle />;
        return <FaChevronRight />; // default
    };

    // Mobile off-canvas
    const [mobileOpen, setMobileOpen] = useState(false);
    const closeMobileMenu = () => setMobileOpen(false);

    const isAuthed = !!auth?.userInfo;

    const handleAccordionToggle = (e) => {
        const d = e.currentTarget;
        if (!d.open) return;
        const root = d.closest('.admin-root');
        root?.querySelectorAll('details[open]').forEach(el => {
            if (el !== d) el.removeAttribute('open');
        });
    };

    // reset state on logout + persist view
    useEffect(() => {
        if (!isAuthed) {
            setViewMode('view');
            localStorage.removeItem('menuAutoSwitched');
        }
    }, [isAuthed]);

    useEffect(() => {
        document.body.classList.toggle('menu-open', mobileOpen);
        return () => document.body.classList.remove('menu-open');
    }, [mobileOpen]);

    // View switch (user/admin) – only admin allowed when logged-in
    const [viewMode, setViewMode] = useState(() => {
        if (typeof window === 'undefined') return 'view';
        return localStorage.getItem('menuView') || 'view';
    });
    useEffect(() => {
        if (!isAuthed) return;

        const alreadySwitched = localStorage.getItem('menuAutoSwitched') === '1';

        if (!alreadySwitched && viewMode !== 'admin') {
            setViewMode('admin');
            localStorage.setItem('menuAutoSwitched', '1');
        }
    }, [isAuthed, viewMode]);
    useEffect(() => {
        if (typeof window !== 'undefined') localStorage.setItem('menuView', viewMode);
    }, [viewMode]);

    useEffect(() => {
        // If logged out, force user view
        if (!auth?.userInfo && viewMode !== 'view') setViewMode('view');
        if (typeof window !== 'undefined') localStorage.setItem('menuView', viewMode);
    }, [auth?.userInfo, viewMode]);

// Only load admin categories when user is logged in AND in "admin" view
    const { getAdminCategories } = useAdminUserCategoriesHook({
        page: 1,
        q: '',
        limit: 50,
        enabled: isAuthed && viewMode === 'admin',
    });

    const adminCategories = getAdminCategories?.data?.categories || [];
    const isLoadingAdmin = getAdminCategories?.isLoading && isAuthed && viewMode === 'admin';

    const { inbox } = useInbox({ page: 1, limit: 1, unreadOnly: true, refetchMs: 30000, enabled: isAuthed });
    const unreadCount = inbox.data?.unread || 0;

    const queryClient = useQueryClient();
    const { mutateAsync: logout } = useMutation(postLogout, {
        onSuccess: () => {
            // Drop every cached query so the next user who logs in on this tab
            // never sees the previous user's cached data (curations, etc.).
            queryClient.clear();
            navigate('/admin/login');
        },
    });
    const handleLogout = async () => {
        await logout({});
    };

    const closeAllFlyouts = () => {
        document.querySelectorAll('.admin-root details[open]')
            .forEach(d => d.removeAttribute('open'));
    };

    // Build admin menu sets
    const { adminNormalMenus, adminDropdownGroups } = useMemo(() => {
        const normal = adminCategories.filter(c => c.menu === 'normal' && c.auth === true);
        const dropdownNames = [...new Set(
            adminCategories
                .map(c => c.menu || '')
                .filter(n => n && n !== 'hidden' && n !== 'normal')
        )];
        const ordered = [
            ...dropdownNames.filter(n => n !== 'account'),
            dropdownNames.includes('account') ? 'account' : null
        ].filter(Boolean);
        const groups = ordered.map(name => ({
            name,
            items: adminCategories.filter(c => c.menu === name)
        }));
        return { adminNormalMenus: normal, adminDropdownGroups: groups };
    }, [adminCategories]);

    const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

    const hasGeneral = adminNormalMenus.length > 0;
    const rootGroups = [
        ...(hasGeneral ? [{ name: '_general', label: t('General'), items: adminNormalMenus }] : []),
        ...adminDropdownGroups.map(g => ({ name: g.name, label: t(cap(g.name)), items: g.items })),
    ];

    // Account helpers
    const user = auth?.userInfo;
    const displayName = user?.name || user?.username || user?.email || 'Account';
    const avatarUrl = user?.avatarUrl || user?.image || user?.photoURL || '';
    const initials = (displayName || '')
        .split(/\s+/)
        .map(s => s[0])
        .join('')
        .slice(0, 2)
        .toUpperCase();

    if (isLoadingAdmin) return <div>Loading...</div>;

    const Sidebar = () => (
        <aside className="menubar">
            <div className="menubar-inner">
                {/* Header */}
                <div className="menubar-top-block">
                    <NavLink
                        to='/'
                        title={t("HomePage")}
                        aria-label={t("HomePage")}
                        onClick={closeMobileMenu}
                    >
                        <img
                            src='/img/spacetime_logo_small.png'
                            alt='logo'
                            className='menubar-logo'
                            width='56'
                            height='56'
                        />
                    </NavLink>
                </div>
                {/* menubar-controls retains only the View/Admin switch */}
                <div className="menubar-controls">
                    {isAuthed && (
                        <div className="menubar-view-switch" role="tablist" aria-label="Menu view">
                            <button
                                type="button"
                                role="tab"
                                id="tab-view"
                                aria-controls="panel-view"
                                className={`switch-btn ${viewMode === 'view' ? 'active' : ''}`}
                                aria-selected={viewMode === 'view'}
                                onClick={() => setViewMode('view')}
                            >
                                {t('View')}
                            </button>

                            <button
                                type="button"
                                role="tab"
                                id="tab-admin"
                                aria-controls="panel-admin"
                                className={`switch-btn ${viewMode === 'admin' ? 'active' : ''}`}
                                aria-selected={viewMode === 'admin'}
                                onClick={() => setViewMode('admin')}
                            >
                                {t('Members')}
                            </button>
                        </div>
                    )}
                </div>
                {/* Links */}
                <div className="menubar-columns">
                    {/* USER VIEW (public/guest) */}
                    {viewMode === 'view' && (
                        <div className="guestbar-links">
                            <NavLink
                                to='/information'
                                className={({ isActive }) => 'nav-link-menu-item' + (isActive ? ' active' : '')}
                                title={t("Information")}
                                aria-label={t("Information")}
                                onClick={closeMobileMenu}
                            >
                                <span className="nav-link-icon"><FaInfoCircle /></span>
                                <span className="nav-link-text guest-link">{t("Information")}</span>
                            </NavLink>
                            <NavLink
                                to='/activities'
                                className={({ isActive }) => 'nav-link-menu-item' + (isActive ? ' active' : '')}
                                title={t("Activities")}
                                aria-label={t("Activities")}
                                onClick={closeMobileMenu}
                            >
                                <span className="nav-link-icon"><FaCalendarAlt /></span>
                                <span className="nav-link-text guest-link">{t("Activities")}</span>
                            </NavLink>
                            <NavLink
                                to='/projects'
                                className={({ isActive }) => 'nav-link-menu-item' + (isActive ? ' active' : '')}
                                title={t("Projects")}
                                aria-label={t("Projects")}
                                onClick={closeMobileMenu}
                            >
                                <span className="nav-link-icon"><FaTools/></span>
                                <span className="nav-link-text guest-link">{t("Projects")}</span>
                            </NavLink>
                        </div>
                    )}

                    {/* ADMIN VIEW (only when logged-in) */}
                    {viewMode === 'admin' && auth?.userInfo && (
                        rootGroups.length === 1 ? (
                            /* Single group: render items inline, already expanded */
                            <div className="sg-inline">
                                <div className="sg-header">
                                    <span className="sg-header-icon">
                                        {iconForAdmin(rootGroups[0].items?.[0] || { name: rootGroups[0].label })}
                                    </span>
                                    <span className="sg-header-label">{rootGroups[0].label}</span>
                                </div>
                                <div className="sg-items">
                                    {rootGroups[0].items.map(item => (
                                        <NavLink
                                            key={item._id}
                                            to={item.path}
                                            className={({ isActive }) => 'sg-item' + (isActive ? ' sg-item--active' : '')}
                                            onClick={closeMobileMenu}
                                        >
                                            {t(item.name)}
                                        </NavLink>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="admin-root">
                                {rootGroups.map(g => (
                                    <details className="root-group" key={g.name} onToggle={handleAccordionToggle}>
                                        <summary className="admin-root-item">
                                            <span className="nav-link-icon">
                                              {iconForAdmin(g.items?.[0] || { name: g.label })}
                                            </span>
                                            <span className="nav-link-text">{g.label}</span>
                                            <FaChevronRight className="arrow" />
                                        </summary>

                                        <aside className="menubar-flyout">
                                            <div className="flyout-header">
                                                <span className="flyout-title">{g.name === '_general' ? t('General') : t(g.label)}</span>
                                                <button
                                                    type="button"
                                                    className="flyout-back-btn"
                                                    onClick={closeAllFlyouts}
                                                >
                                                    ← {t('Back') || 'Back'}
                                                </button>
                                            </div>

                                            <div className="flyout-links" role="menu">
                                                {(g.name === '_general' ? adminNormalMenus : g.items || []).map(item => (
                                                    <NavLink
                                                        key={item._id}
                                                        to={item.path}
                                                        role="menuitem"
                                                        className={({ isActive }) => 'nav-link-menu-item' + (isActive ? ' active' : '')}
                                                        onClick={() => { closeAllFlyouts(); closeMobileMenu(); }}
                                                    >
                                                        <span className="nav-link-text">{t(item.name)}</span>
                                                    </NavLink>
                                                ))}
                                            </div>
                                        </aside>

                                    </details>
                                ))}
                                <div
                                    className="flyout-backdrop"
                                    onClick={() => { closeAllFlyouts(); closeMobileMenu(); }}
                                    aria-hidden="true"
                                />
                            </div>
                        )
                    )}
                </div>

                {/* Bottom: Account / Login */}
                <div className='navbar-nav-options'>
                    <div className='navbar-nav-signin'>
                        {!auth?.userInfo ? (
                            <Link
                                to='/admin/login'
                                className='nav-link-menu-item admin-login button'
                                aria-current='page'
                                onClick={closeMobileMenu}
                            >
                                <FaSignInAlt className='mb-1' />
                                <span className="nav-link-text"> {t("Login")}</span>
                            </Link>
                        ) : (
                            <div className="account-box">
                                <button
                                    className="account-trigger"
                                    type="button"
                                    aria-haspopup="menu"
                                    aria-expanded="false"
                                >
                                    {avatarUrl ? (
                                        <img src={avatarUrl} alt="" className="account-avatar" />
                                    ) : (
                                        <div className="account-avatar">{initials || <FaUser />}</div>
                                    )}
                                    <span className="account-name">{displayName}</span>
                                </button>

                                <div className="account-menu" role="menu" aria-label="Account options">
                                    <button className='logout-button-nav' onClick={handleLogout} role="menuitem">
                                        <FaSignOutAlt className='mb-1' /> {t("Logout")}
                                    </button>
                                    <Link to='/admin' role="menuitem" onClick={closeMobileMenu}>
                                        <FaTachometerAlt className='mb-1' /> {t("Dashboard")}
                                    </Link>
                                    <Link to='/admin/account/profile' role="menuitem" onClick={closeMobileMenu}>
                                        <FaUserCog className='mb-1' /> {t("Edit Profile")}
                                    </Link>
                                    <Link to='/admin/my-notifications' role="menuitem" onClick={closeMobileMenu}>
                                        <FaBell className='mb-1' /> {t("Notifications")}
                                        {unreadCount > 0 && (
                                            <span className="badge bg-danger ms-2" style={{ fontSize: '0.65rem' }}>
                                                {unreadCount > 99 ? '99+' : unreadCount}
                                            </span>
                                        )}
                                    </Link>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Copyright, shown under the profile. The version links to
                        the updates page but stays visually subtle (inherits the
                        muted copyright colour, no underline). */}
                    <div className="menubar-copyright">
                        © {new Date().getFullYear()} SpaceTime -{' '}
                        <Link
                            to='/admin/updates'
                            onClick={closeMobileMenu}
                            title={t('Updates')}
                            style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                            {APP_VERSION}v
                        </Link>
                    </div>
                </div>
            </div>
        </aside>
    );

    return (
        <nav className='navbar navbar-light menu-navbar'>
            {/* Mobile hamburger (top-left on small screens) */}
            <button
                type="button"
                className="mobile-hamburger"
                aria-label={t('Open menu')}
                aria-expanded={mobileOpen}
                onClick={() => setMobileOpen(o => !o)}
            >
                <FaBars />
            </button>

            {/* Click-outside overlay for mobile drawer */}
            {mobileOpen && (
                <div
                    className="mobile-nav-overlay"
                    onClick={closeMobileMenu}
                    aria-hidden="true"
                />
            )}

            {/* Always-on sidebar */}
            <Sidebar />

            {/* Top utilities bar remains available */}
            <div className='container' style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {auth?.userInfo && (<CurationShortcut />)}
                {auth?.userInfo && (<NotificationBell />)}
                <LanguageSwitcher />
            </div>
        </nav>
    );
};

export default Navigation;
